import { useState, useEffect } from "react";
import { Col, Row, Dropdown } from "react-bootstrap";
import { CustomAsyncSelect } from "components/Common/Common";
import CustomEditor from "components/TipTapEditor/CustomeEditor";
import useModal from "hooks/useModal";
import threDots_img from "Assets/images/three-dots.png";
import { AppRadioField } from "components/shared/FormComponents/AppRadioField";
import { STRATEGY_OPTIONS } from "config/constants";
import SaveStrategyTemplatePop from "components/Modals/SaveStrategyTemplatePop";
import { components } from "react-select";
import confirmDialogV2 from "utils/confirmationDialogV2";
import { useLazyGetStartegyTemplatesQuery } from "store/services/clients/reports-v2/recommendationTemplates";
import TaskStrategiesTemplateModal from "../TaskPlanTemplate/StrategyTemplatesPop";
import TaskPlanStrategyModal from "./TaskPlanStrategyModal";
import CommonButton from "components/Common/CommonButton";

export const TaskPlanStrategyForm = ({
    taskIndex,
    strategyIndex,
    setFieldValue,
    values,
    remove,
    setFieldTouched,
    assessmentId,
    clientType,
    isEdit = false,
    errors,
    touched
}) => {

    const { show, data, open, close } = useModal();
    const { show: showTemplates, data: templatesData, open: templatesOpen, close: templatesClose } = useModal();

    const [selectedTemplate, setSelectedTemplate] = useState(null);

    /** ✅ Correct Formik Path */
    const strategyPath = `tasks.${taskIndex}.strategy.${strategyIndex}`;

    /** ✅ Correct Value Access */
    const currentStrategy = values?.tasks?.[taskIndex]?.strategy?.[strategyIndex];

    const handleTemplateSelectFromModal = (template) => {
        if (!template) return;

        const templateOption = {
            label: template.title || template.label,
            value: {
                id: template.id || template.value?.id,
                text: template.text || template.value?.text || "",
                attachments: template.attachments || template.value?.attachments || []
            },
            tags: template.tags || []
        };

        setSelectedTemplate(templateOption);

        setFieldValue(`${strategyPath}.text`, templateOption.value.text);
        setFieldValue(`${strategyPath}.attachments`, templateOption.value.attachments);
        setFieldValue(`${strategyPath}.template_id`, templateOption.value.id);
        setFieldValue(`${strategyPath}.title`, templateOption.label);

        templatesClose();
    };

    const handleTemplateSelect = (selected) => {
        setSelectedTemplate(selected);
        setFieldValue(`${strategyPath}.text`, selected?.value?.answers || "");
        setFieldValue(`${strategyPath}.attachments`, selected?.value?.attachments || []);
        setFieldValue(`${strategyPath}.template_id`, selected?.value?.id);
        setFieldValue(`${strategyPath}.title`, selected?.label);
    };

    const handleDeleteStrategy = async (e) => {
        e.stopPropagation();

        const isConfirm = await confirmDialogV2({
            title: "DELETE STRATEGY",
            message: "Are you sure you want to DELETE this strategy?",
        });

        if (!isConfirm) return;

        if (values?.tasks?.[taskIndex]?.strategy?.length > 1) {
            remove(strategyIndex);
            setFieldTouched(`tasks.${taskIndex}.strategy`, true);
        }
        else {
            setFieldValue(`tasks.${taskIndex}.strategy.${strategyIndex}`, {
                text: "",
                attachments: []
            })
        }

    };

    /** ✅ Sync dropdown when form values change */
    useEffect(() => {

        if (currentStrategy?.template_id && currentStrategy?.title) {

            setSelectedTemplate({
                label: currentStrategy.title,
                value: {
                    id: currentStrategy.template_id,
                    text: currentStrategy.text || "",
                    attachments: currentStrategy.attachments || []
                }
            });

        } else {
            setSelectedTemplate(null);
        }

    }, [
        currentStrategy?.template_id,
        currentStrategy?.title,
        currentStrategy?.text,
        currentStrategy?.attachments
    ]);

    const Option = (props) => {
        const { data, isSelected } = props;

        return (
            <components.Option {...props}>
                <div>
                    <div style={{ color: isSelected ? "#fff" : "#212529" }}>
                        {data.label}
                    </div>

                    {data.tags?.length > 0 && (
                        <div className="mt-1 d-flex flex-wrap gap-1">
                            {data.tags.map((tag, idx) => (
                                <div
                                    key={idx}
                                    className="rounded-pill"
                                    style={{
                                        pointerEvents: "none",
                                        fontWeight: "normal",
                                        backgroundColor: "#e8f5fe",
                                        color: "#007bff",
                                        border: "1px solid #007bff",
                                        padding: "1px 5px",
                                        fontSize: "12px"
                                    }}
                                >
                                    {tag}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </components.Option>
        );
    };

    const [getTemplates] = useLazyGetStartegyTemplatesQuery();

    const loadTemplates = async (search) => {

        try {

            const data = await getTemplates({
                assessmentId,
                search,
                clientType,
                status: ""
            }).unwrap();

            const templates = data?.records || [];

            return templates.map((i) => ({
                label: i.title,
                value: i
            }));

        } catch (error) {
            console.error("Error Getting template options", error);
        }
    };

    const strategyError =
        errors?.tasks?.[taskIndex]?.strategy?.[strategyIndex]?.text;

    const strategyTouched =
        touched?.tasks?.[taskIndex]?.strategy?.[strategyIndex]?.text; 

    return (
        <>
            <SaveStrategyTemplatePop
                mainSetFieldValue={setFieldValue}
                taskIndex={taskIndex}
                strategyIndex={strategyIndex}
                showPopup={show}
                closePopup={close}
                data={data}
            />

            <TaskPlanStrategyModal
                mainSetFieldValue={setFieldValue}
                setSelectedTemplate={handleTemplateSelectFromModal}
                taskIndex={taskIndex}
                strategyIndex={strategyIndex}
                showPopup={showTemplates}
                closePopup={templatesClose}
                data={templatesData}
            />

            <Row className="ps-3">

                <Col lg="12" className="px-0" style={{ borderBottom: "0.75px solid #000" }}>

                    <div className="d-flex justify-content-between align-items-center">

                        <h6 className="m-0 proxima themeBlue fw-bold text-uppercase" style={{ fontSize: "12px" }}>
                            STRATEGY* {strategyIndex + 1}
                        </h6>

                        {strategyIndex >= 0 && !isEdit && (
                            <div className="cstm-dropdownclass">
                                <Dropdown>
                                    <Dropdown.Toggle id="dropdown-basic">
                                        <img width={19} height={18} src={threDots_img} alt="dots" />
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={handleDeleteStrategy}>
                                            Delete
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}

                    </div>

                </Col>

                <Col xs="4" lg="4" className="px-0" style={{ margin: "12px 0" }}>
                    <p style={{ color: "#071e3e", fontStyle: "italic" }}>
                        Provide detailed strategies that would address the behavior or skill. Indicate clearly what you are trying to achieve with the strategies you are recommending.
                    </p>
                </Col>

                <Col xs="8" lg="8" style={{ margin: "12px 0" }}>

                    {currentStrategy?.text || currentStrategy?.strategyRadio === "newStrategy" ? (

                        <>
                            {(currentStrategy?.template_id || selectedTemplate) && (
                                <div className="reports-v2 gap-3">

                                    <h6 className="proxima themeBlue" style={{ fontSize: "12px" }}>
                                        Strategy Template
                                    </h6>

                                    <CustomAsyncSelect
                                        cstmOption={Option}
                                        placeholder="(no template selected)"
                                        loadOptions={loadTemplates}
                                        onChange={handleTemplateSelect}
                                        value={selectedTemplate}
                                        defaultOptions
                                        isClearable
                                    />

                                </div>
                            )}

                            <div className="ck-editor-wrapper my-2">

                                <CustomEditor
                                    name={`${strategyPath}.text`}
                                    attachmentName={`${strategyPath}.attachments`}
                                />
                                {
                                    !isEdit && !currentStrategy?.template_id &&
                                    <CommonButton
                                        className="staff-button-filled"
                                        text="Save to Templates"
                                        type="button"
                                        onClick={() => {                                            
                                            open({
                                                // name: values[fieldName][strategyIndex]?.name,
                                                answers: currentStrategy.text,
                                            });
                                        }}
                                    />
                                }


                            </div>

                        </>

                    ) : (

                        <div className="session-input-radio text-area-report">

                            <AppRadioField
                                options={STRATEGY_OPTIONS}
                                name={`${strategyPath}.strategyRadio`}
                            />

                            <button
                                type="button"
                                disabled={!currentStrategy?.strategyRadio}
                                className="blue-discharge-screen-btn mt-3"
                                onClick={() => {

                                    if (currentStrategy?.strategyRadio === "new") {

                                        setFieldValue(`${strategyPath}.strategyRadio`, "newStrategy");
                                        setFieldValue(`${strategyPath}.text`, "");
                                        setFieldValue(`${strategyPath}.attachments`, []);
                                        setFieldValue(`${strategyPath}.template_id`, "");
                                        setFieldValue(`${strategyPath}.title`, "");

                                        setSelectedTemplate(null);

                                    } else {
                                        templatesOpen();
                                    }

                                }}
                            >
                                Continue
                            </button>

                        </div>

                    )}

                    {!(currentStrategy?.text || currentStrategy?.strategyRadio === "newStrategy") &&
                        (strategyTouched) &&
                        strategyError && (
                            <div className="text-danger small mt-2">
                                {strategyError}
                            </div>
                        )}

                </Col>

            </Row>
        </>
    );
};
